import React, { useState, useRef, useEffect } from 'react';
import { animated, useSpring } from 'react-spring';
import { FiChevronDown } from 'react-icons/fi';
import { BeatLoader } from 'react-spinners';
import styled from 'styled-components';

// Components
import Modal from '../organisms/Modal';

// API 
import { saveRsvps } from '../../api/rsvps';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    max-width: 1200px;
    width: 100%;
    margin: 0 auto;
    column-gap: 8px;
    row-gap: 24px;

    @media (max-width: 480px) {
        flex-direction: column;
        margin-bottom: 8px;
        max-width: 90%;
        margin-left: auto;
        margin-right: auto;
    }
`;

const FullColumn = styled.div`
    flex: 1;
    width: 100%;

    @media (max-width: 480px) {
        width: 100%;
        flex: 1;
    }
`;

const HalfColumn = styled.div`
    width: 50%;

    @media (max-width: 480px) {
        width: 100%;
    }
`;

const RsvpCard = styled.div`
    border-radius: 12px;
    background-color: #eaeaea;
    width: auto; height: auto;
    padding: 18px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    position: relative;
`;

const RsvpCardLoadingOverlay = styled.div(props => ({
    position: 'absolute',
    top: 0, left: 0,
    width: '100%', height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    borderRadius: '12px', zIndex: 5,
    opacity: props.isSaving ? 1 : 0,
    transition: 'all 0.3s ease',
    pointerEvents: props.isSaving ? 'auto' : 'none'
}));

const RsvpCardTitle = styled.h2`
    margin: 0;
`;

const RsvpCardDescription = styled.p`
    margin-top: 4px;
    margin-bottom: 6px;
    font-size: 15px;
    line-height: 1.75;

    @media (max-width: 480px) {
        font-size: 16px;
        line-height: 1.5;
    }
`;

const RsvpCardDivider = styled.hr`
    width: 100%;
    margin: 12px auto;
    background-color: #f8f8f8;
`;

const RsvpCardFormContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    flex-wrap: wrap;
`;

const CheckboxLabelContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const StatusChipContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    flex: 1;
`;

const RsvpCardFormCheckboxContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    margin-bottom: 12px;

    @media (min-width: 480px) {
        flex-direction: row;
        align-items: center;
    }
`;

const RsvpCardFormCheckboxInput = styled.input`
    margin-right: 8px;
    cursor: pointer;
`;

const RsvpCardFormCheckboxLabel = styled.label`
    margin: 0;
    font-size: 18px;
    cursor: pointer;
`;

const RsvpCardSubmitButtonContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
    margin-top: 8px;
`;

const RsvpCardSubmitButton = styled.button`
    padding: 8px 16px;
    border-radius: 8px;
    background-color: #56050D;
    border: none;
    color: #f8f8f8;
    font-size: 18px;
    font-weight: 700;
    font-family: Lexend, sans-serif;
    cursor: pointer;

    &:hover {
        background-color: #3a0309;
    }

    &:disabled {
        background-color: #848383;
        cursor: not-allowed;
    }
`;

const RsvpCardDeclineLinkButton = styled.button`
    padding: 0;
    border: none;
    background-color: transparent;
    color: #83231A;
    font-size: 18px;
    font-weight: 700;
    font-family: Lexend, sans-serif;
    cursor: pointer;
    margin-top: 8px;
    text-decoration: underline;
    
    &:hover {
        color: #6e0b12;
    }

    &:disabled {
        color: #848383;
        cursor: not-allowed;
        text-decoration: none;
    }

    @media (max-width: 480px) {
        margin-top: 16px;
        margin-bottom: 8px;
        font-size: 16px;
    }
`;

const RsvpCardSpecialRequestsButton = styled.button`
    color: ${props => (props.disabled ? '#848383' : '#3498db')};
    border: none;
    background: none;
    cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};

    @media (max-width: 480px) {
        margin-top: 8px;
        font-size: 16px;
    }
`;

const RsvpCardSpecialRequestsTextarea = styled.textarea`
    width: calc(100% - 20px);
    height: 100px;
    padding: 10px;
    font-size: 16px;
    border-radius: 4px;
    border: 1px solid #ccc;
    resize: vertical;
`;

const Card = styled.div`
    border: 8px solid #56050D;
    border-radius: 12px;
    margin-bottom: ${props => (props.isOpen ? '32px' : '16px')};
    transition: all 0.3s ease;
`;

const CardHeader = styled.div`
    padding: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    background-color: ${props => (props.isOpen ? '#56050D' : 'transparent')};
    transition: all 0.3s ease;
    flex-wrap: wrap;

    &:hover {
        background-color: ${props => (props.isOpen ? '#3a0309' : '#f0f0f0')};
    }
`;

const CardTitleContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const CardTitle = styled.div`
    font-size: 24px;
    font-weight: bold;
    color: ${props => (props.isOpen ? '#fff' : '#56050D')};
    transition: all 0.3s ease;
`;

const CardSubtitle = styled.div(props => ({
    fontSize: '16px',
    marginTop: '8px',
    color: props.isOpen ? '#fff' : '#8a8a8a',
    transition: 'all 0.3s ease',
}));

const CardBody = styled(animated.div)`
    padding: 24px;
    overflow: hidden;

    @media (max-width: 480px) {
        padding: 2px;
    }
`;

const CardBodyText = styled.p`
    margin-top: 0px;
    margin-bottom: 6px;
    font-size: 15px;
    line-height: 1.75;

    & > span > ul {
        margin-top: -12px;
        margin-bottom: 12px;
        white-space: pre-line;
        height: auto;
        padding-top: 0px;
        padding-bottom: 0px;

        // Reduce space between list items
        & > li {
            list-style-type: disc;
            margin-left: 0px;
            margin-bottom: -24px;
            font-size: 15px;
            line-height: 1.5;
        }
    }

    @media (max-width: 480px) {
        font-size: 16px;
        line-height: 1.5;

        & > span > ul {
            margin-top: -12px;
            margin-bottom: 12px;
            white-space: pre-line;
            height: auto;
            padding-top: 0px;
            padding-bottom: 0px;

            // Reduce space between list items
            & > li {
                list-style-type: disc;
                margin-left: 0px;
                margin-bottom: -24px;
                font-size: 16px;
                line-height: 1.5;
            }
        }
    }
`;

const CardBodyRenderedText = styled.span`
    line-height: 1.75;

    & > ul {
        margin-top: -12px;
        margin-bottom: 12px;
        white-space: pre-line;
        height: auto;
        padding-top: 0px;
        padding-bottom: 0px;

        // Reduce space between list items
        & > li {
            list-style-type: disc;
            margin-left: 0px;
            margin-bottom: -24px;
            font-size: 18px;
        }
    }
`;

const StatusChip = styled.div(({ status }) => ({
    padding: '4px 32px',
    borderRadius: '16px',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: 'Lexend, sans-serif',
    textTransform: 'uppercase',
    letterSpacing: '4px',
    color: getStatusChipTextColor(status),
    backgroundColor: getStatusChipBackgroundColor(status),

    '@media (max-width: 480px)': {
        fontSize: '14px',
        padding: '4px 32px',
        width: '100%',
        textAlign: 'center',
        order: 2,
        marginLeft: 0,
        marginTop: '8px',
        marginBottom: '12px',
        marginRight: 0,
    },
}));

const InlineLink = styled.span`
    color: #3498db;
    text-decoration: underline;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    &:hover {
        color: #2a80b9;
    }
`;

const getStatusChipTextColor = (status) => {
    switch (status) {
        case 'ATTENDING':
            return '#19723E';
        case 'NOT_ATTENDING':
            return '#83231A';
        default:
            return '#848383';
    }
}

const getStatusChipBackgroundColor = (status) => {
    switch (status) {
        case 'ATTENDING':
            return '#2ECC71';
        case 'NOT_ATTENDING':
            return '#E74C3C';
        default:
            return '#D9D9D9';
    }
}

const getStatusChipText = (status) => {
    switch (status) {
        case 'ATTENDING':
            return 'Accepted';
        case 'NOT_ATTENDING':
            return 'Declined';
        default:
            return 'No Response';
    }
}

const EventCard = ({ personId, invitation, onChangeRsvp }) => {
    const contentRef = useRef();

    const family = invitation.family;
    const event = invitation.event;
    const rsvps = invitation.rsvps;
    const currentPerson = family.people.find(person => person.id === personId);

    const currentPersonRsvp = rsvps.find(rsvp => rsvp.person.id === personId);
    const status = currentPersonRsvp ? currentPersonRsvp.status : 'PENDING';

    const [peopleRsvps, setPeopleRsvps] = useState([]); // [ { person: {}, attending: boolean }]
    const [initialRsvps, setInitialRsvps] = useState([]);
    const [isRSVPChanged, setIsRSVPChanged] = useState(false);
    const [rsvpSpecialRequests, setRsvpSpecialRequests] = useState({});
    const [initialRsvpSpecialRequests, setInitialRsvpSpecialRequests] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [rsvpError, setRsvpError] = useState(null); 

    // UI labels
    const [acceptedPeopleNames, setAcceptedPeopleNames] = useState('');
    const [rejectedPeopleNames, setRejectedPeopleNames] = useState('');
    const [formattedEventDate, setFormattedEventDate] = useState('');
    const [rsvpFormSuccessMessage, setRsvpFormSuccessMessage] = useState('');

    // Guest inputs
    const [adultCount, setAdultCount] = useState(0);
    const [childCount, setChildCount] = useState(0);

    // Contact modal
    const [contactModalOpen, setContactModalOpen] = useState(false);

    const [triggerRerender, setTriggerRerender] = useState(0);

    const bodyHeight = contentRef.current ? contentRef.current.clientHeight : 0;
    const { transform, height, paddingTop, paddingBottom } = useSpring({
        transform: `rotate(${isOpen ? 180 : 0}deg)`,
        height: isOpen ? bodyHeight : 0,
        paddingTop: isOpen ? '24px' : '0px',
        paddingBottom: isOpen ? '24px' : '0px',
    });

    const isAllDeclinedInitially = initialRsvps.length > 0 && initialRsvps.every(rsvp => rsvp.status === 'NOT_ATTENDING');
    const familyHasKids = family && family.people && family.people.some(person => person.role === 'Child');
    const adultOnlyEvent = parseInt(invitation.max_kids) === 0;
    
    // This useEffect checks if any RSVP status has changed from the initial state, 
    // and updates the 'isRSVPChanged' state accordingly. It also checks if any person 
    // is attending when RSVPs are initially empty, and sets 'isRSVPChanged' to true if so.
    useEffect(() => {
        if (peopleRsvps.length > 0 && initialRsvps.length > 0) {
            const hasAnyRSVPChanged = peopleRsvps.some((personRsvp) => {
                const initialRsvp = initialRsvps.find(rsvp => rsvp.person.id === personRsvp.person.id);
                const initialAttendingStatus = initialRsvp && initialRsvp.status === 'ATTENDING';
                return personRsvp.attending !== initialAttendingStatus;
            });
            const hasAnySpecialRequestChanged = Object.keys(initialRsvpSpecialRequests).some(personId => {
                return rsvpSpecialRequests[personId] !== initialRsvpSpecialRequests[personId];
            });
            const hasAnySpecialRequestBeenAdded = Object.keys(rsvpSpecialRequests).some(personId => {
                return initialRsvpSpecialRequests[personId] === undefined && rsvpSpecialRequests[personId] !== '';
            });
            setIsRSVPChanged(hasAnyRSVPChanged || hasAnySpecialRequestChanged || hasAnySpecialRequestBeenAdded);
        } else if (peopleRsvps.length > 0 && initialRsvps.length === 0) {
            const anyAttending = peopleRsvps.some(personRsvp => personRsvp.attending);
            setIsRSVPChanged(anyAttending);
        }
    }, [peopleRsvps, initialRsvps, rsvpSpecialRequests, initialRsvpSpecialRequests]);

    // This useEffect updates the peopleRsvps state with each person's attendance 
    // status whenever the invitation data changes. It also sets the initialRsvps 
    // state to the current rsvps if initialRsvps is initially empty.
    useEffect(() => {
        if (invitation && family && family.people && family.people.length > 0) {
            setPeopleRsvps(family.people.map(person => {
                const rsvp = rsvps.find(rsvp => rsvp.person.id === person.id);
                return {
                    person,
                    attending: (rsvp && rsvp.status === 'ATTENDING') || false
                }
            }));
            if (initialRsvps.length === 0) { setInitialRsvps(rsvps); }
            const specialRequests = rsvps.reduce((acc, rsvp) => {
                if (rsvp.comments) {
                    acc[rsvp.person.id] = rsvp.comments;
                }
                return acc;
            }, {});

            const acceptedPeopleRsvps = rsvps.filter(rsvp => rsvp.status === 'ATTENDING' && (!adultOnlyEvent || rsvp.person.role === 'Adult'));
            const acceptedPeopleNames = acceptedPeopleRsvps.map(rsvp => rsvp.person.name.split(' ')[0]).join(', ');
            setAcceptedPeopleNames(acceptedPeopleNames);

            const rejectedPeopleRsvps = rsvps.filter(rsvp => rsvp.status === 'NOT_ATTENDING' && (!adultOnlyEvent || rsvp.person.role === 'Adult'));
            const rejectedPeopleNames = rejectedPeopleRsvps.map(rsvp => rsvp.person.name.split(' ')[0]).join(', ');
            setRejectedPeopleNames(rejectedPeopleNames);

            setRsvpSpecialRequests(specialRequests);
            setInitialRsvpSpecialRequests(specialRequests);
        }
    }, [invitation, family, rsvps]);

    useEffect(() => {
        if (adultCount > invitation.max_adults) {
            console.warn(`You can only have ${invitation.max_adults} adult${invitation.max_adults > 1 ? 's' : ''} attend.`);
            setAdultCount(invitation.max_adults);
        }

        if (childCount > invitation.max_kids && !adultOnlyEvent) {
            console.warn(`You can only have ${invitation.max_kids} child${invitation.max_kids > 1 ? 'ren' : ''} attend.`);
            setChildCount(invitation.max_kids);
        }

        if (adultOnlyEvent && childCount > 0) {
            console.warn(`This is an adult-only event.`);
            setChildCount(0);
        }
    }, [adultCount, childCount]);

    useEffect(() => {
        if (event.date) {
            const dateObj = new Date(event.date);
            const options = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
            const formattedDate = dateObj.toLocaleDateString('en-US', options);
            setFormattedEventDate(formattedDate);
        }
    }, [event.date]);

    useEffect(() => {
        setTriggerRerender(prev => prev + 1);
    }, [rsvpSpecialRequests]);

    useEffect(() => {
        if (rsvpFormSuccessMessage && rsvpFormSuccessMessage !== '') {
            setTimeout(() => {
                setRsvpFormSuccessMessage('');
            }, 5000);
        }
    }, [rsvpFormSuccessMessage]);

    const handleSaveRSVP = async (overrideRsvps) => {
        setIsSaving(true);
        setRsvpError(null);

        // Array that will hold ONLY the updated RSVPs
        let updatedRsvpsList = [];
        
        // Logic to check if any RSVPs have been updated
        // accounting for undefined initialRsvp status
        console.log("🚀 ~ handleSaveRSVP ~ peopleRsvps:", peopleRsvps)
        if (!overrideRsvps) {
            for (let i = 0; i < peopleRsvps.length; i++) {
                const personRsvp = peopleRsvps[i];
                const initialPersonRsvp = initialRsvps.find(rsvp => rsvp.person.id === personRsvp.person.id);
                const initialAttendingStatus = initialPersonRsvp && initialPersonRsvp.status === 'ATTENDING';
                if (personRsvp.attending !== initialAttendingStatus) {
                    updatedRsvpsList.push(personRsvp);
                }
            }
        } else {
            updatedRsvpsList = overrideRsvps;
        }

        // If no RSVPs have been updated, return early
        console.log("🚀 ~ handleSaveRSVP ~ updatedRsvpsList:", updatedRsvpsList)
        if (updatedRsvpsList.length === 0) {
            setIsRSVPChanged(false);
            setInitialRsvps(peopleRsvps.map(personRsvp => {
                return {
                    person: personRsvp.person,
                    status: personRsvp.attending ? 'ATTENDING' : 'NOT_ATTENDING'
                }
            }));
            setInitialRsvpSpecialRequests(rsvpSpecialRequests);
            setRsvpFormSuccessMessage('Your RSVP has been saved.');
            setIsSaving(false);
            onChangeRsvp();
            return;
        }

        // Transform the updatedRsvpsList to match the API request body
        updatedRsvpsList = updatedRsvpsList.map(personRsvp => {
            const personId = personRsvp.person.id;
            const attending = personRsvp.attending;
            const rsvpSpecialRequest = rsvpSpecialRequests[personId] || '';
            return {
                invitation_id: invitation.id,
                person_id: personId,
                attending: attending,
                comments: rsvpSpecialRequest
            }
        });
        console.log("🚀 ~ handleSaveRSVP ~ updatedRsvpsList:", updatedRsvpsList)

        try {
            await saveRsvps(updatedRsvpsList);
            setIsRSVPChanged(false);
            if (!overrideRsvps) {
                setInitialRsvps(peopleRsvps.map(personRsvp => {
                    return {
                        person: personRsvp.person,
                        status: personRsvp.attending ? 'ATTENDING' : 'NOT_ATTENDING'
                    }
                }));
            } else {
                setInitialRsvps(overrideRsvps.map(personRsvp => {
                    return {
                        person: personRsvp.person,
                        status: personRsvp.attending ? 'ATTENDING' : 'NOT_ATTENDING'
                    }
                }));
            }
            setInitialRsvpSpecialRequests(rsvpSpecialRequests);
            setRsvpFormSuccessMessage('Your RSVP has been saved.');
            onChangeRsvp();
        } catch (error) {
            console.error("🚀 ~ file: EventCard.jsx:270 ~ handleSaveRSVP ~ error", error);
            // Reset the RSVPs to the initial state
            setPeopleRsvps(initialRsvps.map(rsvp => {
                return {
                    person: rsvp.person,
                    attending: rsvp.status === 'ATTENDING'
                }
            }));
        } finally {
            setIsSaving(false);
        }
    }

    const handleRsvpCheckboxChange = (e, person) => {
        const adultsAttending = peopleRsvps.filter(rsvp => rsvp.person.role === 'Adult' && rsvp.attending).length;
        const kidsAttending = peopleRsvps.filter(rsvp => rsvp.person.role === 'Child' && rsvp.attending).length;

        if (adultOnlyEvent && person.role === 'Child') {
            setRsvpError('This is an adult-only event.');
            return;
        }

        if (person.role === 'Adult' && e.target.checked && adultsAttending >= invitation.max_adults) {
            setRsvpError(`You can only have ${invitation.max_adults} adult${invitation.max_adults > 1 ? 's' : ''} attend.`);
            return;
        }

        if (person.role === 'Child' && e.target.checked && kidsAttending >= invitation.max_kids) {
            setRsvpError(`You can only have ${invitation.max_kids} child${invitation.max_kids > 1 ? 'ren' : ''} attend.`);
            return;
        }

        setRsvpError(null);

        setPeopleRsvps(peopleRsvps.map(rsvp => {
            if (rsvp.person.id === person.id) {
                return {
                    person: rsvp.person,
                    attending: e.target.checked
                }
            }
            return rsvp;
        }));
    }

    const handleSpecialRequestClick = (personId) => {
        setRsvpSpecialRequests(prevSpecialRequests => ({
            ...prevSpecialRequests,
            [personId]: '',
        }));
    };

    const handleSpecialRequestChange = (event, personId) => {
        const { value } = event.target;
        setRsvpSpecialRequests(prevSpecialRequests => ({
            ...prevSpecialRequests,
            [personId]: value,
        }));
    };

    const handleCancelSpecialRequestClick = (personId) => {
        setRsvpSpecialRequests(prevSpecialRequests => {
            const newSpecialRequests = { ...prevSpecialRequests };
            delete newSpecialRequests[personId];
            return newSpecialRequests;
        });
    };

    const handleDeclineForAll = async () => {
        const confirmDecline = window.confirm(`Are you sure you want to decline for the ${event.name} event?`);
        if (confirmDecline) {
            const newPeopleRsvps = peopleRsvps.map(rsvp => {
                return { person: rsvp.person, attending: false }
            });

            setPeopleRsvps(newPeopleRsvps);
    
            // Call the API to save the RSVP status
            await handleSaveRSVP(newPeopleRsvps);
        }
    }

    const convertTo12Hour = (time) => {
        const [hour, minute] = time.split(':');
        const today = new Date();
        return new Date(
            today.getFullYear(), 
            today.getMonth(),
            today.getDate(),
            hour,
            minute
        ).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    if (currentPerson && currentPerson.role === 'Child' && adultOnlyEvent) {
        return null;
    }

    return (
        <>
            <Card isOpen={isOpen}>
                <CardHeader isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
                    <CardTitleContainer>
                        <CardTitle isOpen={isOpen}>{event.name}</CardTitle>
                        <CardSubtitle isOpen={isOpen}>{formattedEventDate}</CardSubtitle>
                        {acceptedPeopleNames.length > 0 && (
                            <CardSubtitle isOpen={isOpen} style={{ color: isOpen ? '#8cb89e' : '#19723E', fontWeight: 'bold' }}>Accepted: {acceptedPeopleNames}</CardSubtitle>
                        )}
                        {rejectedPeopleNames.length > 0 && (
                            <CardSubtitle isOpen={isOpen} style={{ color: isOpen ? '#c1918c' : '#83231A', fontWeight: 'bold' }}>Declined: {rejectedPeopleNames}</CardSubtitle>
                        )}
                    </CardTitleContainer>
                    
                    <animated.div style={{ transform }}>
                        <FiChevronDown size={24} color={isOpen ? '#fff' : '#56050D'} style={{ transition: 'all 0.3s ease' }} />
                    </animated.div>
                </CardHeader>
                <CardBody style={{ height, paddingTop, paddingBottom }}>
                    <Container ref={contentRef}>
                        <Row style={{ columnGap: '24px' }}>
                            <HalfColumn>
                                <CardBodyText><strong>Date:</strong> {formattedEventDate}</CardBodyText>
                                <CardBodyText><strong>Time:</strong> {convertTo12Hour(event.start_time)} - {convertTo12Hour(event.end_time)} CST</CardBodyText>
                                
                                <CardBodyText>
                                    <strong>Location: </strong> 
                                    <a href={`https://www.google.com/maps/search/?api=1&query=${event.street}, ${event.city}, ${event.state} ${event.zipcode}`} target="_blank" rel="noopener noreferrer">
                                        {event.street}, {event.city}, {event.state} {event.zipcode}
                                    </a>
                                </CardBodyText>
                                
                                {event.details && (
                                    <CardBodyText>
                                        <strong>Event Details: </strong>
                                        <CardBodyRenderedText dangerouslySetInnerHTML={{ __html: event.details }} />
                                    </CardBodyText>
                                )}

                                {event.dress_code && (
                                    <CardBodyText style={{ padding: 12, backgroundColor: '#d8d8d8', borderRadius: 12 }}>
                                        <strong>Dress Code: </strong>
                                        <CardBodyRenderedText dangerouslySetInnerHTML={{ __html: event.dress_code }} />
                                    </CardBodyText>   
                                )}
                            </HalfColumn>
                            <HalfColumn>
                                <RsvpCard>
                                    <RsvpCardLoadingOverlay isSaving={isSaving}>
                                        <BeatLoader color="#56050D" loading={isSaving} size={24} cssOverride={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
                                    </RsvpCardLoadingOverlay>
                                    <RsvpCardTitle>RSVP to Event</RsvpCardTitle>
                                    <RsvpCardDescription>{(familyHasKids && adultOnlyEvent) && (<strong style={{ fontSize: 18 }}>Adults Only (21+) <br /></strong>)}Please select who will be attending. If someone has any special requests such as allergies to foods,  accessibility needs, or any other preferences, please let us know in the 'Special Requests' section or <InlineLink onClick={() => setContactModalOpen(true)}>contact us.</InlineLink></RsvpCardDescription>
                                    <RsvpCardDivider />
                                    <RsvpCardFormContainer>
                                        <Container>
                                            {(rsvpSpecialRequests != null) && family && family.people && family.people.map(person => {
                                                if (adultOnlyEvent && person.role === 'Child') { return null; }
                                                const personRsvp = peopleRsvps.find(rsvp => rsvp.person.id === person.id);
                                                const initialPersonRsvp = initialRsvps.find(rsvp => rsvp.person.id === person.id);
                                                const attending = personRsvp ? personRsvp.attending : false;
                                                const personHasSpecialRequest = rsvpSpecialRequests[person.id] !== undefined;
                                                const personStatus = initialPersonRsvp && initialPersonRsvp.status ? initialPersonRsvp.status : 'PENDING';

                                                return (
                                                    <>
                                                        <Row key={person.id}>
                                                            <RsvpCardFormCheckboxContainer>
                                                                <CheckboxLabelContainer>
                                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-start' }}>
                                                                        <RsvpCardFormCheckboxInput type="checkbox" checked={attending} onChange={(e) => handleRsvpCheckboxChange(e, person)} />
                                                                        <RsvpCardFormCheckboxLabel onClick={() => handleRsvpCheckboxChange({ target: { checked: !attending } }, person)}>{person.name}</RsvpCardFormCheckboxLabel>
                                                                    </div>
                                                                    {!personHasSpecialRequest && (
                                                                        <RsvpCardSpecialRequestsButton disabled={!attending} onClick={() => handleSpecialRequestClick(person.id)}>Have special requests?</RsvpCardSpecialRequestsButton>
                                                                    )}
                                                                </CheckboxLabelContainer>
                                                                <StatusChipContainer>
                                                                    <StatusChip status={personStatus}>{getStatusChipText(personStatus)}</StatusChip>
                                                                </StatusChipContainer>
                                                            </RsvpCardFormCheckboxContainer>
                                                        </Row>
                                                        {personHasSpecialRequest && (
                                                            <Row style={{ marginBottom: 24, flexDirection: 'column', rowGap: 12 }}>
                                                                <label style={{ fontWeight: 'bold', fontSize: 13, letterSpacing: 0.5 }}>Special requests for {person.name.split(' ')[0]}:</label>
                                                                <RsvpCardSpecialRequestsTextarea disabled={!attending} value={rsvpSpecialRequests[person.id]} onChange={(e) => handleSpecialRequestChange(e, person.id)} placeholder={`I am allergic to...\nI need accessibility accommodations...`} />
                                                                <RsvpCardSpecialRequestsButton onClick={() => handleCancelSpecialRequestClick(person.id)}>Cancel</RsvpCardSpecialRequestsButton>
                                                            </Row>   
                                                        )}
                                                    </>
                                                );
                                            })}
                                        </Container>
                                        
                                        {rsvpError && (
                                            <p style={{ color: '#83231A', fontWeight: 'bold', fontSize: '16px', marginTop: '8px', marginBottom: '0px' }}>{rsvpError}</p>
                                        )}

                                        {rsvpFormSuccessMessage && (
                                            <p style={{ color: '#19723E', fontWeight: 'bold', fontSize: '16px', marginTop: '8px', marginBottom: '8px' }}>{rsvpFormSuccessMessage}</p>
                                        )}

                                        {/* Submit Button */}
                                        <RsvpCardSubmitButtonContainer>
                                            <RsvpCardSubmitButton onClick={() => { handleSaveRSVP() }} disabled={!isRSVPChanged}>Save RSVP</RsvpCardSubmitButton>
                                            <RsvpCardDeclineLinkButton onClick={handleDeclineForAll} disabled={isAllDeclinedInitially}>Decline{family && family.people && family.people.length > 1 ? " for All" : ""}</RsvpCardDeclineLinkButton>
                                        </RsvpCardSubmitButtonContainer>
                                    </RsvpCardFormContainer>
                                </RsvpCard>
                            </HalfColumn>
                        </Row>
                    </Container>
                </CardBody>
            </Card>
            <Modal isOpen={contactModalOpen} onClose={() => setContactModalOpen(false)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 0, fontSize: 22 }}>Contact</h3>
                    <button onClick={() => setContactModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>✖</button>
                </div>
                <hr />
                <ul>
                    <li style={{ marginBottom: 16 }}>Bikash Dhali: <a href="tel:8473408257">847-340-8257</a></li>
                    <li>Rina Bala: <a href="tel:8473479240">847-347-9240</a></li>
                </ul>
            </Modal>
        </>
    )
                                        
}

export default EventCard;